import { router, usePage } from '@inertiajs/react';
import { ChevronDown, ChevronUp, Filter, Plus, Search } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import {
  PATIENT_CREATE_EVENT,
  PATIENT_CREATED_EVENT,
  type PatientCreatedEventDetail,
} from '@/lib/patientEvents';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Heading } from '@/components/ui/heading';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { Patient } from '@/components/patient/types';
import Layout from '../../layouts/Layout';

type Pagination = {
  current_page: number;
  total_pages: number;
  total_count: number;
  next_page: number | null;
  prev_page: number | null;
};

type RansackQuery = {
  name_or_email_cont?: string;
  s?: string;
};

type PageProps = Record<string, unknown>;

type PatientWithDemo = Patient & { demo?: boolean };

type PatientsIndexProps = {
  name: string;
  patients: PatientWithDemo[];
  pagination: Pagination;
  q: RansackQuery;
  errors?: string[];
};

export default function PatientsIndex({
  name,
  patients,
  pagination,
  q,
}: PatientsIndexProps) {
  const [patientList, setPatientList] = useState<PatientWithDemo[]>(patients);
  const [search, setSearch] = useState(q?.name_or_email_cont || '');

  const page = usePage<PageProps>();
  const isFirstSearchRender = useRef(true);
  const currentCount = patientList.length;
  const totalCount = pagination?.total_count ?? currentCount;

  const getStatus = (patient: PatientWithDemo) => {
    const status = patient.status || (patient.last_sign_in_at ? 'active' : 'invited');
    switch (status) {
      case 'disabled':
        return 'Disattivato';
      case 'active':
        return 'Attivo';
      case 'invited':
      default:
        return 'Invitato';
    }
  };

  const getStatusVariant = (patient: PatientWithDemo) =>
    patient.disabled_at ? 'destructive' : patient.status === 'active' ? 'default' : 'secondary';

  useEffect(() => setPatientList(patients), [patients]);

  useEffect(() => {
    const [, queryString] = (page.url || '').split('?');
    if (!queryString) return;
    const params = new URLSearchParams(queryString);
    if (params.get('new') === '1') {
      window.dispatchEvent(new CustomEvent(PATIENT_CREATE_EVENT));
      router.replace({
        url: '/therapists/patients',
        preserveScroll: true,
        preserveState: true,
      });
    }
  }, [page.url]);

  // ordinamento
  const currentSort = q?.s || 'first_name asc';
  const isNameAsc = currentSort === 'first_name asc';
  const isNameDesc = currentSort === 'first_name desc';
  const handleSortName = () => {
    const nextSort = isNameAsc ? 'first_name desc' : 'first_name asc';
    router.get(
      '/therapists/patients',
      { q: { ...q, s: nextSort }, page: 1 },
      { preserveState: true },
    );
  };

  const handleResetFilters = () => {
    setSearch('');
    router.get(
      '/therapists/patients',
      { q: { ...q, name_or_email_cont: '', s: currentSort }, page: 1 },
      { preserveState: true },
    );
  };

  const handleSearch = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    router.get(
      '/therapists/patients',
      { q: { ...q, name_or_email_cont: search, s: currentSort }, page: 1 },
      { preserveState: true, replace: true },
    );
  };

  const goToPage = (pageNumber: number) => {
    router.get(
      '/therapists/patients',
      { q: { ...q, s: currentSort }, page: pageNumber },
      { preserveState: true },
    );
  };

  useEffect(() => {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<PatientCreatedEventDetail>;
      const newPatient = customEvent.detail?.patient;
      if (!newPatient) return;
      setPatientList((prev) => {
        const withoutDuplicate = prev.filter((p) => p.id !== newPatient.id);
        const next = [newPatient, ...withoutDuplicate];
        return next.sort((a, b) => {
          const aName = (a.display_name || a.first_name || '').toLocaleLowerCase();
          const bName = (b.display_name || b.first_name || '').toLocaleLowerCase();
          if (aName === bName) return 0;
          return aName > bName ? 1 : -1;
        });
      });
    };
    window.addEventListener(PATIENT_CREATED_EVENT, handler as EventListener);
    return () => window.removeEventListener(PATIENT_CREATED_EVENT, handler as EventListener);
  }, []);

  // auto-search on input change
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (isFirstSearchRender.current) {
        isFirstSearchRender.current = false;
        return;
      }
      router.get(
        '/therapists/patients',
        { q: { ...q, name_or_email_cont: search, s: currentSort }, page: 1 },
        { preserveState: true, replace: true },
      );
    }, 300);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, currentSort]);

  const triggerNewPatientModal = () => window.dispatchEvent(new CustomEvent(PATIENT_CREATE_EVENT));

  return (
    <Layout isAdmin user={{ name }}>
      <div className="flex w-full flex-col gap-6">
        <div className="space-y-2">
          <Heading>Clienti</Heading>
          <p className="text-sm text-muted-foreground">
            Gestisci i tuoi clienti e visualizza le loro informazioni.
          </p>
        </div>

        <form
          className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
          onSubmit={handleSearch}
        >
          <div className="relative w-full sm:max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="search"
              placeholder="Cerca per nome, email o ID..."
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
              className="pl-9"
              name="q[name_or_email_cont]"
              autoComplete="off"
              type="search"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label="Azzera filtri"
              onClick={handleResetFilters}
            >
              <Filter className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="default"
              size="sm"
              className="gap-2"
              onClick={triggerNewPatientModal}
            >
              <Plus className="h-4 w-4" />
              Nuovo Cliente
            </Button>
          </div>
        </form>

        <Card>
          <CardHeader>
            <div>
              <CardTitle headingLevel="h2">Lista Clienti</CardTitle>
              <CardDescription>
                {search
                  ? `${currentCount} clienti trovati per "${search}"`
                  : `${totalCount} clienti totali`}
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[80px]">ID</TableHead>
                    <TableHead
                      role="button"
                      onClick={handleSortName}
                      className="cursor-pointer select-none"
                      title="Ordina per nome"
                    >
                      <div className="flex items-center gap-1">
                        Nome
                        {isNameAsc && <ChevronDown className="h-4 w-4" />}
                        {isNameDesc && <ChevronUp className="h-4 w-4" />}
                        {!isNameAsc && !isNameDesc && (
                          <ChevronDown className="h-4 w-4 opacity-20" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead className="w-[120px]">Stato</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {patientList.length > 0 ? (
                    patientList.map((patient) => {
                      const displayName =
                        patient.display_name ||
                        [patient.first_name, patient.last_name].filter(Boolean).join(' ') ||
                        patient.email;
                      return (
                        <TableRow
                          key={patient.id}
                          role="link"
                          tabIndex={0}
                          className="cursor-pointer"
                          onClick={() => router.visit(`/therapists/patients/${patient.id}`)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              router.visit(`/therapists/patients/${patient.id}`);
                            }
                          }}
                        >
                          <TableCell className="font-medium">#{patient.id}</TableCell>
                          <TableCell className="font-medium">
                            <span className="text-primary hover:underline">{displayName}</span>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {patient.email}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {patient.demo ? (
                                <Badge variant="outline" className="text-amber-300 border-amber-400/50">
                                  Demo
                                </Badge>
                              ) : null}
                              <Badge variant={getStatusVariant(patient)}>{getStatus(patient)}</Badge>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                        Nessun cliente trovato. Prova a cambiare i filtri o crea un nuovo cliente.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="space-y-3 md:hidden">
              {patientList.length > 0 ? (
                patientList.map((patient) => {
                  const displayName =
                    patient.display_name ||
                    [patient.first_name, patient.last_name].filter(Boolean).join(' ') ||
                    patient.email;
                  return (
                    <Card
                      key={patient.id}
                      variant="outlined"
                      className="p-4 cursor-pointer"
                      role="link"
                      tabIndex={0}
                      onClick={() => router.visit(`/therapists/patients/${patient.id}`)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          router.visit(`/therapists/patients/${patient.id}`);
                        }
                      }}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="space-y-2">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>#{patient.id}</span>
                        {patient.demo ? (
                          <Badge variant="outline" className="text-amber-300 border-amber-400/50">
                            Demo
                          </Badge>
                        ) : null}
                        <Badge variant={getStatusVariant(patient)}>{getStatus(patient)}</Badge>
                      </div>
                          <span className="font-medium leading-tight text-primary hover:underline">
                            {displayName}
                          </span>
                          <p className="text-sm text-muted-foreground break-all">{patient.email}</p>
                        </div>
                      </div>
                    </Card>
                  );
                })
              ) : (
                <Card variant="flat" className="p-6 text-center text-muted-foreground">
                  Nessun cliente trovato. Prova a cambiare i filtri o crea un nuovo cliente.
                </Card>
              )}
            </div>
          </CardContent>

          {pagination && pagination.total_pages > 1 && (
            <CardFooter className="justify-center gap-2 pt-4">
              <Button
                variant="ghost"
                size="sm"
                disabled={pagination.current_page === 1}
                onClick={() => goToPage(pagination.current_page - 1)}
              >
                «
              </Button>
              {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map((pageNumber) => (
                <Button
                  key={pageNumber}
                  variant={pageNumber === pagination.current_page ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => goToPage(pageNumber)}
                  disabled={pageNumber === pagination.current_page}
                >
                  {pageNumber}
                </Button>
              ))}
              <Button
                variant="ghost"
                size="sm"
                disabled={pagination.current_page === pagination.total_pages}
                onClick={() => goToPage(pagination.current_page + 1)}
              >
                »
              </Button>
            </CardFooter>
          )}
        </Card>
      </div>
    </Layout>
  );
}
